// Right-side drawer (sheet) for on-demand forms — keeps browse views uncluttered
// by moving create/mutate flows off the main canvas. Self-contained (no portal
// lib); overlay + ESC-to-close mirror ConfirmDialog. Styled by _shared/shared.css
// (.adm-drawer*).

import { useEffect } from 'react';
import type { ReactNode } from 'react';

export interface DrawerProps {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Optional tone accent on the header rail (danger for destructive flows). */
  tone?: 'primary' | 'danger' | 'neutral';
  /** Sticky footer actions (buttons). */
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Disable overlay-click / ESC close while a request is in flight. */
  busy?: boolean;
  width?: number;
}

export function Drawer({ open, title, subtitle, tone = 'primary', footer, onClose, children, busy, width = 480 }: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;
  return (
    <div className="adm-drawer-overlay" onClick={() => !busy && onClose()}>
      <aside
        className={`adm-drawer adm-drawer--${tone}`}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="adm-drawer-head">
          <div className="adm-drawer-head-text">
            <h2 className="adm-drawer-title">{title}</h2>
            {subtitle && <div className="adm-drawer-subtitle text-tertiary">{subtitle}</div>}
          </div>
          <button className="adm-drawer-close" aria-label="關閉" onClick={onClose} disabled={busy}>
            ✕
          </button>
        </header>
        <div className="adm-drawer-body">{children}</div>
        {footer && <footer className="adm-drawer-foot">{footer}</footer>}
      </aside>
    </div>
  );
}
